import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { useTheme } from '../../context/shared/ThemeContext'
import { Patient } from '../../shared/types/src';
import { useAuth } from '../../context/shared/AuthContext'
// import { useNotification } from '../../hooks/useNotification';
import apiService from '../../services/shared/apiService';

interface ScheduleReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  patients: Patient[];
  selectedPatientIds: string[];
}

const ScheduleReportModal: React.FC<ScheduleReportModalProps> = ({ isOpen, onClose, patients, selectedPatientIds }) => {
  const { themeClasses } = useTheme();
  const { user } = useAuth();
  // const { addToast } = useNotification();
  const [frequency, setFrequency] = useState<'weekly' | 'monthly'>('weekly');
  const [content, setContent] = useState<string[]>(['symbolic_trends', 'alerts']);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPatientNames = patients
    .filter(p => selectedPatientIds.includes(p.uid))
    .map(p => p.name)
    .join(', ');

  const handleContentChange = (item: string) => {
    setContent(prev =>
      prev.includes(item)
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  const handleSaveSchedule = async () => {
    if (!user?.uid) {
        setError("You must be logged in to save a schedule.");
        return;
    }
    setIsSaving(true);
    setError(null);
    try {
        const schedulePayload = {
            patientIds: selectedPatientIds,
            frequency,
            content,
            therapistId: user.uid,
        };
        await apiService.saveReportSchedule(user.uid, schedulePayload);
        // addToast('Report schedule saved successfully!', 'success');
        onClose();
    } catch (err) {
        setError("Failed to save schedule. Please try again.");
        console.error(err);
        // addToast(err.message || 'Failed to save schedule.', 'error');
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2 text-gray-900">
          Schedule New Report
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          Automate report generation for: <span className="font-semibold">{selectedPatientNames}</span>
        </p>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium">Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as 'weekly' | 'monthly')}
              className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md ${themeClasses.input}`}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Report Content</label>
            <div className="mt-2 space-y-2">
                {['symbolic_trends', 'alerts', 'messages'].map(item => (
                    <div key={item} className="flex items-center">
                        <input
                            id={`content-${item}`}
                            type="checkbox"
                            checked={content.includes(item)}
                            onChange={() => handleContentChange(item)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`content-${item}`} className="ml-3 block text-sm capitalize">
                            {item.replace('_', ' ')}
                        </label>
                    </div>
                ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end space-x-3">
          <button onClick={onClose} type="button" className={`${themeClasses.buttonSecondary}`} disabled={isSaving}>
            Cancel
          </button>
          <button onClick={handleSaveSchedule} type="button" className={`${themeClasses.buttonPrimary}`} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
        {error && <p className="text-sm text-red-500 mt-2 text-right">{error}</p>}
      </div>
    </Modal>
  );
};

export default ScheduleReportModal; 